import { asString, normalizeStringArray } from "./parse-properties";
import { findH4Body, splitH3Entries } from "./parse-sections";
import type { DataFlow, FlowStep } from "./types";

const STEP_LINE = /^(\d+)\.\s+\*?\*?(.+?)\*?\*?\s*(?:→|->)\s*\*?\*?(.+?)\*?\*?\s*:\s*"?(.+?)"?$/;

function parseStepsBody(body: string): FlowStep[] {
  const steps: FlowStep[] = [];
  for (const raw of body.split("\n")) {
    const line = raw.trim();
    if (line.length === 0) continue;
    const m = line.match(STEP_LINE);
    if (!m) {
      if (/^\d+\./.test(line)) {
        console.warn("[arch-parser] could not parse flow step:", line);
      }
      continue;
    }
    const numStr = m[1];
    const source = (m[2] ?? "").trim().replace(/^"|"$/g, "");
    const target = (m[3] ?? "").trim().replace(/^"|"$/g, "");
    const description = (m[4] ?? "").trim().replace(/^"|"$/g, "");
    if (numStr === undefined || source.length === 0 || target.length === 0) continue;
    steps.push({
      number: Number(numStr),
      source,
      target,
      description,
    });
  }
  return steps;
}

function parseNotesBody(body: string): string[] {
  const out: string[] = [];
  for (const raw of body.split("\n")) {
    const m = raw.match(/^\s*-\s+(.+)$/);
    if (!m) continue;
    let text = (m[1] ?? "").trim();
    if (text.startsWith('"') && text.endsWith('"')) text = text.slice(1, -1);
    if (text.length > 0) out.push(text);
  }
  return out;
}

function parseTopProps(body: string): Record<string, unknown> {
  const props: Record<string, unknown> = {};
  for (const raw of body.split("\n")) {
    if (/^####\s/.test(raw)) break;
    const m = raw.match(/^\s*-\s+([a-zA-Z_][a-zA-Z0-9_-]*)\s*:\s*(.*)$/);
    if (!m) continue;
    const key = m[1];
    const rawValue = (m[2] ?? "").trim();
    if (key === undefined || rawValue.length === 0) continue;
    if (rawValue.startsWith("[") && rawValue.endsWith("]")) {
      const inner = rawValue.slice(1, -1).trim();
      props[key] =
        inner.length === 0
          ? []
          : inner
              .split(",")
              .map((s) => s.trim().replace(/^"|"$/g, "").replace(/^'|'$/g, ""))
              .filter((s) => s.length > 0);
      continue;
    }
    let scalar: unknown = rawValue;
    if (
      (rawValue.startsWith('"') && rawValue.endsWith('"')) ||
      (rawValue.startsWith("'") && rawValue.endsWith("'"))
    ) {
      scalar = rawValue.slice(1, -1);
    }
    props[key] = scalar;
  }
  return props;
}

export function parseDataFlows(sectionBody: string): DataFlow[] {
  const entries = splitH3Entries(sectionBody);
  return entries.map((entry) => {
    const id = entry.title.trim();
    const top = parseTopProps(entry.body);
    const stepsBody = findH4Body(entry.body, "Steps") ?? "";
    const notesBody = findH4Body(entry.body, "Notes") ?? "";
    const steps = parseStepsBody(stepsBody);
    const notes = parseNotesBody(notesBody);
    return {
      id,
      label: asString(top.label) ?? id,
      description: asString(top.description),
      why: asString(top.why),
      actors: normalizeStringArray(top.actors),
      tags: normalizeStringArray(top.tags),
      steps,
      notes,
    };
  });
}
